import {createSelector} from '@reduxjs/toolkit'

import {eq} from '@shared/helpers/lang'

import {
    ApiResult,
    ChargePointPolicy,
    PayMethod,
    PayMoneyUserAvailablePayStates,
    SubscribePayMethodType,
} from '@web/core/constants'
import {getPayMoneyAPIInfo} from '@web/helpers/policies'

import {PaymoneyMemberType, RootState} from './interfaces'
import {isPayMoneyUser as checkPayMoneyUser} from './utils'

/** 충전 포인트 Selector */
export const chargePointSelector = (state: RootState) => state.chargePoint

export const payMoneyInfoSelector = createSelector(
    chargePointSelector,
    ({apiResult, payMoney, reloadable, requiredMethod, policy}) => {
        const {
            isPayMoneyRequestSucceed,
            isChargeLimitRequestSucceed,
            isChargeLimitRequestRetryAvailable,
            isChargeLimitRequestFailed,
        } = getPayMoneyAPIInfo(payMoney.apiResult, reloadable.apiResult)
        const eqResultStatus = eq(apiResult)
        const isPayMoneyUser = checkPayMoneyUser(payMoney.userStatus)
        return {
            payMoneyUserStatus: payMoney.userStatus,
            // 4.0, 4,1, 4.2 (페이머니 가입자)
            isPayMoneyUser,
            // 1.0, 3.0, 3.1, 3.2, 4.0 (결제가능)
            availablePayment: PayMoneyUserAvailablePayStates.includes(payMoney.userStatus),
            /** 페이머니 연결 통장 타입 */
            bankCode: payMoney.bankCode,
            /** 페이머니 연결 통장명 */
            bankName: payMoney.bankName,
            /** 페이머니 회원조회 API 결과 타입 */
            payMoneyUserResultType: payMoney.apiResult,
            /** 페이머니 충전한도 조회 API 결과 타입 */
            chargeLimitResultType: reloadable.apiResult,
            /** 충전포인트 잔액 조회 성공 */
            isChargePointRequestSucceed: eqResultStatus(ApiResult.SUCCESS),
            /** 충전포인트 잔액 조회 실패 + 재조회 불가 */
            isChargePointRequestFailed: eqResultStatus(ApiResult.FAILED),
            /** 충전포인트 잔액 조회 실패 + 재조회 가능 */
            isChargePointRequestFailedRetryAvailable: eqResultStatus(ApiResult.FAILED_RETRY_AVAILABLE),
            /** 페이머니 회원조회 API 성공 */
            isPayMoneyRequestSucceed,
            /** 충전한도 조회 API 성공 */
            isChargeLimitRequestSucceed,
            /** 충전한도 조회 API 재시도 가능 실패 */
            isChargeLimitRequestRetryAvailable,
            /** 충전한도 조회 API 재시도 불가 실패 */
            isChargeLimitRequestFailed,
            /** 미래에셋 머니통장 여부 */
            isMiraeassetPayMoneyBank: payMoney.bankCode === PaymoneyMemberType.MIRAEASSET,
            /** 충전결제 진행에 반드시 충전결제 필요 여부 */
            isChargeAccountRequired: requiredMethod === 'CHARGE_ACCOUNT',
            /** 충전결제 진행에 페이머니 잔액만 있으면 충전결제 필요없는 케이스 */
            isPayMoneyOnlyRequired: requiredMethod === 'PAY_MONEY_BALANCE',
            /** 충전포인트 취급정책 */
            chargePointPolicy: policy,
            /**
             * 충전포인트를 페이머니처럼 취급하는지 여부
             * - 결제수단금액
             * - 충전포인트 사용금액
             * - 충전결제 충전금액
             * 등의 로직에 영향 끼침
             **/
            isUsedLikePayMoney: isPayMoneyUser || eq(policy, ChargePointPolicy.PAYMONEY),
        }
    },
)

export type AdapterState = {
    rechargePoint?: {
        /** 포인트 충전 사용 여부 */
        rechargePointEnabled?: boolean
        /** 충전할 포인트 금액 */
        rechargingAmount?: number
    }
    selectedMethod: SubscribePayMethodType
}

export const adapterChargePointSelector = createSelector(
    chargePointSelector,
    ({recharging: {amount: rechargingAmount}}): AdapterState => {
        const selectedMethod = PayMethod.NONE

        return {
            rechargePoint: {
                rechargePointEnabled: false,
                rechargingAmount,
            },
            selectedMethod,
        }
    },
)
